import React, {useEffect, useMemo, useRef, useState} from 'react';
import 'moment/locale/pt-br';
import MomentUtils from '@date-io/moment';
import {MuiPickersUtilsProvider} from '@material-ui/pickers';
import {makeStyles} from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import {GetAppOutlined, SaveOutlined} from '@material-ui/icons';
import PageWrapper from '../../../shared/pagewrapper/PageWrapper.js';
import PageTitle from '../../../shared/pagetitle/PageTitle.js';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import Message from '../../../shared/message/Message.js';
import ListaErros from '../../../shared/listaerros/ListaErros.js';
import ContainedButton from '../../../shared/buttons/ContainedButton.tsx';
import OutlinedButton from '../../../shared/buttons/OutlinedButton.tsx';
import LabelValue from '../../../shared/labelvalue/LabelValue.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {useToast} from '../../../shared/toast/ToastProvider.tsx';
import PageNaoEncontrada from '../../../PageNaoEncontrada.jsx';
import SeletorArquivoXlsx from '../../components/cargasaf/SeletorArquivoXlsx.jsx';
import {CampoDataChamada, CampoProcessoSeletivo} from '../../components/cargasaf/CamposCargaSaf.jsx';
import {
    CONFIRMACAO_SALVAR_CARGA_PBEST,
    comChave,
    filtroCpf,
    formatarCpfCarga,
    formatarDataCarga,
    formatarNota,
    formatarPercentual,
    lerErroDeArquivo,
    mensagemCargaPbestSalva,
    montarPayloadLeituraPbest,
    montarPayloadSalvarPbest,
    podeSalvarPbest,
    SEM_REGISTROS_PBEST,
    separarMensagens
} from '../../components/cargasaf/cargaSaf.js';
import {ouTraco} from '../../components/inscricao/documentoInscricao.js';
import useCarregamentoPagina from '../../components/useCarregamentoPagina.js';
import {PAGINACAO_PADRAO} from '../../components/paginacao.js';
import {salvarArquivoResposta} from '../../../../utils/download.js';
import useCheckModule from '../../../../hooks/useCheckModule.js';
import useFetch from '../../../../hooks/useFetch.js';
import useCreate from '../../../../hooks/useCreate.js';
import {
    URL_CARGAS_SAF_APOIO_PROCESSOS_SELECAO,
    URL_CARGAS_SAF_PBEST,
    URL_CARGAS_SAF_PBEST_LEITURA,
    URL_CARGAS_SAF_PBEST_MODELO
} from '../../../../constants/index.js';
import {containerPaper} from '../../../../utils/styles.js';

const PARAMS_VAZIOS = {};
const FILTER_OPTIONS = {filterByColumn: true};
const TABLE_CONTAINER_PROPS = {component: 'div'};
const NOME_MODELO = 'modelo_carga_pbest.xlsx';
const COLUNAS_MODELO = 'Processo Seletivo, Data da Chamada, Nº Chamada, Inscrição, CPF, Nome, Cód. Campus, '
    + 'Cód. Curso, Turno, Percentual e Nota ENEM';

const useStyles = makeStyles(theme => ({
    ...containerPaper(theme),
    acoes: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(2),
        alignItems: 'center'
    }
}));

/*
 * Carga Pbest e outros (módulo PBEST): o usuário escolhe o processo seletivo e a data da
 * chamada e envia a planilha xlsx. Ao selecionar o arquivo, o back lê a planilha e a tela
 * mostra a prévia; "Salvar Arquivo" grava a carga (os candidatos passam a valer para a
 * inscrição). Erros de leitura/validação da planilha (400, uma mensagem por linha) ficam
 * numa lista na página; os demais erros já são mostrados pelos hooks.
 */
export default function PageCargaPbest() {
    const classes = useStyles();
    const checkModule = useCheckModule();
    const podeAcessar = checkModule('PBEST');
    const confirmation = useConfirmationDialog();
    const {createToast} = useToast();
    const [fetchApoio, carregandoApoio] = useFetch({returnInnerLoadingState: true});
    const [baixarModelo, baixandoModelo] = useFetch({returnInnerLoadingState: true, responseType: 'blob'});
    const [ler, lendo] = useCreate({returnInnerLoadingState: true});
    const [salvar, salvando] = useCreate({returnInnerLoadingState: true});

    const [processos, setProcessos] = useState([]);
    const [idProcesso, setIdProcesso] = useState('');
    const [dataChamada, setDataChamada] = useState(null);
    const [arquivo, setArquivo] = useState(null);
    const [linhas, setLinhas] = useState([]);
    const [erros, setErros] = useState([]);
    // Descarta a resposta de uma leitura já substituída (novo arquivo ou "Remover").
    const leituraRef = useRef(0);

    useEffect(() => {
        if (podeAcessar) {
            fetchApoio(URL_CARGAS_SAF_APOIO_PROCESSOS_SELECAO, PARAMS_VAZIOS,
                dados => setProcessos(Array.isArray(dados) ? dados : []));
        }
    }, [podeAcessar]);

    const linhasComChave = useMemo(() => comChave(linhas), [linhas]);

    const limparArquivo = () => {
        leituraRef.current += 1;
        setArquivo(null);
        setLinhas([]);
        setErros([]);
    };

    const limparTela = () => {
        limparArquivo();
        setIdProcesso('');
        setDataChamada(null);
    };

    // Texto de várias linhas (uma crítica por linha) vai para a lista da página; o resto, para o tratamento padrão.
    const mostrarErro = (erro, tratarErro) => {
        if (typeof erro === 'string' && erro.trim()) {
            setErros(separarMensagens(erro));
        } else {
            tratarErro(erro);
        }
    };

    const selecionarArquivo = selecionado => {
        const leitura = ++leituraRef.current;
        setArquivo(selecionado);
        setLinhas([]);
        setErros([]);

        ler(URL_CARGAS_SAF_PBEST_LEITURA, montarPayloadLeituraPbest(selecionado), resposta => {
            if (leitura === leituraRef.current) {
                setLinhas(Array.isArray(resposta?.linhas) ? resposta.linhas : []);
            }
        }, (erro, tratarErro) => {
            // Planilha inválida: o arquivo sai da tela e as críticas (uma por linha) vão para a lista da página.
            if (leitura === leituraRef.current) {
                setArquivo(null);
                mostrarErro(erro, tratarErro);
            }
        });
    };

    const baixar = () => baixarModelo(URL_CARGAS_SAF_PBEST_MODELO, PARAMS_VAZIOS,
        blob => salvarArquivoResposta({data: blob, headers: {}}, NOME_MODELO),
        (erro, tratarErro) => lerErroDeArquivo(erro).then(tratarErro));

    const gravar = () => {
        setErros([]);
        salvar(URL_CARGAS_SAF_PBEST, montarPayloadSalvarPbest({idProcessoSeletivo: idProcesso, dataChamada, arquivo}),
            resposta => {
                createToast({severity: 'success', message: mensagemCargaPbestSalva(resposta?.quantidade)});
                limparTela();
            },
            mostrarErro);
    };

    const confirmarSalvar = () => confirmation(CONFIRMACAO_SALVAR_CARGA_PBEST, gravar);

    // Loading da página pelo LoadingModal global (o BlockUI fica só nas tabelas).
    useCarregamentoPagina(carregandoApoio || baixandoModelo || lendo || salvando);

    if (!podeAcessar) {
        return <PageNaoEncontrada/>;
    }

    const ocupado = carregandoApoio || baixandoModelo || lendo || salvando;

    return (
        <PageWrapper>
            <PageTitle title='Carga Pbest e outros'/>
            <Paper className={classes.containerPaper}>
                <MuiPickersUtilsProvider utils={MomentUtils} locale='pt-br'>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Message type='info' variant='standard'>
                                Envie a planilha xlsx com as colunas, nesta ordem: {COLUNAS_MODELO}. A primeira
                                linha da planilha é o cabeçalho. Use o modelo para conferir o layout.
                            </Message>
                        </Grid>
                        <CampoProcessoSeletivo valor={idProcesso} opcoes={processos} onChange={setIdProcesso}/>
                        <CampoDataChamada valor={dataChamada} onChange={setDataChamada}/>
                        <Grid item xs={12}>
                            <SeletorArquivoXlsx
                                id='arquivoCargaPbest'
                                rotulo='Arquivo Excel (xlsx) *'
                                arquivo={arquivo}
                                onSelecionar={selecionarArquivo}
                                onRemover={limparArquivo}
                                disabled={ocupado}/>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.acoes}>
                                <OutlinedButton startIcon={<GetAppOutlined/>} disabled={ocupado} onClick={baixar}>
                                    Baixar modelo do Excel
                                </OutlinedButton>
                                <ContainedButton
                                    startIcon={<SaveOutlined/>}
                                    disabled={ocupado || !podeSalvarPbest({idProcessoSeletivo: idProcesso, dataChamada, arquivo})}
                                    onClick={confirmarSalvar}>
                                    Salvar Arquivo
                                </ContainedButton>
                            </div>
                        </Grid>
                        {!!erros.length && (
                            <Grid item xs={12}>
                                <ListaErros titulo='Foram encontrados problemas na planilha:' itens={erros}/>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <LabelValue label='Prévia da carga'>
                                <DataGrid
                                    rows={linhasComChave}
                                    comparatorKeys={['chave']}
                                    filterOptions={FILTER_OPTIONS}
                                    paginate
                                    paginateOptions={PAGINACAO_PADRAO}
                                    tableContainerProps={TABLE_CONTAINER_PROPS}
                                    noRecordsText={SEM_REGISTROS_PBEST}
                                    columns={[
                                        {label: 'Proc. Seletivo', field: 'processoSeletivo', filterable: false, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Data Chamada', field: 'dataChamada', filterable: false, valueGetter: valor => formatarDataCarga(valor)},
                                        {label: 'PS Inscrição', field: 'inscricao', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {
                                            label: 'CPF', field: 'cpf', filterable: true,
                                            valueGetter: valor => formatarCpfCarga(valor),
                                            customFilter: (termo, valor) => filtroCpf(termo, valor)
                                        },
                                        {label: 'Nome Aprovado', field: 'nome', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Cód. Curso', field: 'codigoCurso', filterable: true, valueGetter: valor => ouTraco(valor)},
                                        {label: 'Percentual', field: 'percentual', filterable: false, valueGetter: valor => formatarPercentual(valor)},
                                        {label: 'Nota', field: 'nota', filterable: false, valueGetter: valor => formatarNota(valor)}
                                    ]}/>
                            </LabelValue>
                        </Grid>
                    </Grid>
                </MuiPickersUtilsProvider>
            </Paper>
        </PageWrapper>
    );
}
